import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client.js';
import { outbox } from '../../offline/index.js';

// The engineer's schedule for a day. The last copy is kept on the phone, so
// with no signal they still see the day (marked as a saved copy). It
// refreshes while open, so what the other engineer does shows up here too.
const EVERY = 30_000;
const KEY = (day) => `nu.schedule.${day}`;
const read = (day) => {
  try { return JSON.parse(localStorage.getItem(KEY(day)) || 'null'); } catch { return null; }
};
const write = (day, data) => {
  try {
    localStorage.setItem(KEY(day), JSON.stringify({ data, savedAt: new Date().toISOString() }));
    // Keep a week or so: drop older days.
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k?.startsWith('nu.schedule.') && k.slice(12) < new Date(Date.now() - 8 * 864e5).toISOString().slice(0, 10)) localStorage.removeItem(k);
    }
  } catch { /* storage full or blocked: the live copy still shows */ }
};

export function useSchedule(day) {
  const [state, setState] = useState(() => {
    const c = read(day);
    return { data: c?.data || null, savedAt: c ? c.savedAt : null, error: null, live: false };
  });
  const load = useCallback(async () => {
    try {
      const data = await api(`/my/schedule?day=${day}`);
      write(day, data);
      setState({ data, savedAt: null, error: null, live: true });
    } catch (e) {
      const c = read(day);
      setState({ data: c?.data || null, savedAt: c?.savedAt || null, error: e, live: false });
    }
  }, [day]);
  useEffect(() => {
    const c = read(day);
    setState({ data: c?.data || null, savedAt: c?.savedAt || null, error: null, live: false });
    load();
  }, [day, load]);
  useEffect(() => {
    const visible = () => document.visibilityState === 'visible';
    const tick = setInterval(() => { if (visible() && navigator.onLine) load(); }, EVERY);
    const onShow = () => { if (visible()) load(); };
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('online', onShow);
    const off = outbox.subscribe(load);
    return () => { clearInterval(tick); document.removeEventListener('visibilitychange', onShow); window.removeEventListener('online', onShow); off(); };
  }, [load]);
  return { ...state, reload: load };
}
